import { observer } from "mobx-react";
import {
  CompanySearchDialogState,
  extractPortfolioContactsAdminUrl,
  NotConnectedTabState,
  PortfolioCompaniesTab,
  PortfolioType,
  PreparedPageState,
  ReadyState,
  RequestAccessType,
  requestAccessTypeDescriptionLabel,
  requestAccessTypeIcons,
  requestAccessTypeTitleLabel,
  SelectedPortfolioOption,
  SignShareholderRegistersForm,
  toCompaniesWithAdminDbotAccess,
  toCompaniesWithDbotAccess,
  usePortfolio,
} from "@/portfolio/portfolioDomain";
import { CompanyTable } from "@/views/Pages/Portfolio/CompanyTable";
import {
  useDispatch,
  useI18n,
  useLanguage,
  useRootStore,
  useTranslation,
  useUserPrefs,
} from "@/stores/RootStoreContext";
import { BottomSpacer, Box, Container, Grid, GridItem, VBox } from "@/components-v2/Box";
import { EmptyState } from "@/components-v2/EmptyState";
import { Button, LinkButton } from "@/components-v2/Button";
import { Tabs } from "@/components-v2/Tabs";
import { isMobileView } from "@/utils/screen";
import { tokenizeFilterString } from "@/utils/filter";
import { MTextInput } from "@/components-v2/TextInput";
import { DecisionsByCategory, getDecisionsByCategory } from "@/dbot/projectList/projectListDomain";
import { isFailure, isOk, isPending, Result } from "@/common/common";
import { Dialog } from "@/components-v2/Dialog";
import { Alert } from "@/components-v2/Alert";
import { Markdown } from "@/text/Markdown";
import {
  ConfirmDissolveDialog,
  InvalidPortfolioView,
  IsEmailVerifiedAlert,
  PortfolioSettingsDialog,
} from "@/portfolio/portfolioComponents";
import {
  InvitationResponse,
  OnboardingCandidateResponse,
  PortfolioCompany,
} from "@/stores/PortfolioStore";
import { DecisionType } from "@/common/decisions";
import { Checkbox } from "@/components-v2/Checkbox";
import { DecisionLabel } from "@/dbot/projectList/projectListComponents";
import { isBlank, isNotBlank } from "@/utils/strings";
import { Autocomplete } from "@/components-v2/Autocomplete";
import {
  ActionsForNotConnectedCompanies,
  AddCompanyToPortfolioActions,
  CompanySearchActions,
  companySearchDialogTitle,
  filterCompaniesForAddDialog,
  filteredUserCompanies,
  filterSelectableCompanies,
  groupCompaniesByPortfolio,
  hasAdminAccess,
  initPortfolioCompaniesPage,
  leavePortfolioCompaniesPage,
  isDisabledOptionSelected,
  isNoActiveSubscription,
  isPortfolioOwner,
  NewProjectActions,
  PortfolioCompaniesActions,
  PortfolioCompaniesSelectProps,
  PortfolioPageActions,
  prepareCompaniesWithSubscriptionStatus,
  prepareState,
  prepPortfolioQuickSelector,
  SignShareholderRegistersActions,
} from "./companiesDomain";
import { FC, ReactElement, useEffect } from "react";
import { SuggestedCompany } from "@/types/CompanyInfo";
import { SearchResultTableRow } from "@/views/Pages/Portfolio/CompanySearch";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { isPreparedPageState } from "@/signing/signDocumentDomain";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { useAppBar } from "@/appBar/useAppBar";
import { DropdownMenu, DropdownMenuItemType } from "@/components-v2/Dropdown";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { AppBar } from "@/components-v2/AppBar";
import { isProductionEnvironment } from "@/utils/environments";
import { formatPercent } from "@/utils/numbers";
import { MSelect, Select } from "@/components-v2/Select";
import { MDateInput } from "@/components-v2/DateInput";
import { AccessType } from "@/types/enums/AccessType";
import { allFeaturesIncluded, SubscriptionStatus, SubscriptionType } from "@/common/subscription";
import { Icon } from "@/components-v2/Icon";
import { SigningCompleteDialog } from "@/portfolio/signatures/signaturesComponents";
import { fromQueryString, singleValue, toQueryString } from "@/utils/queryParams";
import { getRoleNameByLanguage } from "@/portfolio/contacts/customRoleDomain";
import { MarkdownEditor } from "@/text/MarkdownEditor";
import { RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { TMessageKey } from "@/i18n/types.gen";
import { getRoleTypeLabel } from "@/types/enums/RoleType";
import { MRadioButtonGroup, RadioButton } from "@/components-v2/RadioButton";
import { InfoButton } from "@/components-v2/InfoButton.tsx";
import { HighlightText } from "@/text/HighlightText.tsx";
import { CollapseList } from "@/dbot/common/commonComponents.tsx";
import { QuickFilterBar } from "@/components-v2/QuickFilterBar.tsx";

export const PortfolioCompaniesPage = observer(function PortfolioCompaniesInitializer({
  companiesTab,
  ...props
}: {
  portfolioType: PortfolioType;
  portfolioId?: string;
  companiesTab?: PortfolioCompaniesTab;
}) {
  const { portfolio, onRemovePortfolio, onUpdatePortfolio, header, currentUrl } =
    usePortfolio(props);
  const rootStore = useRootStore();
  const { messageStore, userStore, apiService } = rootStore;
  const dispatch = useDispatch();
  const locale = useLanguage();
  const location = useRootStore().location;
  const queryParams = fromQueryString(location.search);
  const closeSigningCompleteDialog = () => {
    const updatedUrl = `${location.pathname}${toQueryString({
      ...queryParams,
      "signing-complete": undefined,
      "signing-complete-company": undefined,
    })}`;
    dispatch([{ type: "navigate", url: updatedUrl, replace: true }]);
  };
  const companyName: string = singleValue(queryParams["signing-complete-company"]) ?? "";
  const initialShowCompletedSignedDialog = queryParams["signing-complete"] === "true";
  useEffect(() => {
    dispatch(
      initPortfolioCompaniesPage({
        dispatch,
        currentUrl,
        portfolio,
        companiesTab,
        signDocumentCounts: userStore.signDocumentCounts,
        store: rootStore,
        language: locale,
      }),
    );
  }, [
    dispatch,
    portfolio,
    companiesTab,
    header,
    currentUrl,
    rootStore,
    userStore.signDocumentCounts,
    locale,
  ]);
  useEffect(() => () => dispatch(leavePortfolioCompaniesPage), [dispatch]);
  const i18n = useI18n();
  const state = prepareState({
    store: rootStore,
    dispatch,
    i18n,
    portfolio,
    onRemovePortfolio,
    onUpdatePortfolio,
    locale,
    setCustomRoleId: userStore.setCustomRoleId,
    apiService,
  });
  return (
    <PortfolioCompaniesView
      state={state}
      alertStore={messageStore}
      showSigningCompleteDialog={initialShowCompletedSignedDialog}
      closeSigningCompleteDialog={closeSigningCompleteDialog}
      companyName={companyName}
      portfolio={portfolio}
    />
  );
});

const CompaniesView = observer(
  ({ state, actions }: { state: ReadyState; actions: PortfolioPageActions }) => {
    const hasMultipleCompanies: boolean = state.userCompaniesInPortfolio.length !== 1;
    const hasUserCompaniesInPortfolio =
      (state.userCompaniesInPortfolio.length ?? 0) > 0 || !state.portfolioSearchModel.isEmpty;
    return (
      <>
        {state.portfolio.type === "portfolio-instance" && (
          <Box bg borderBottom dropShadow>
            <Container maxWidth="xl" bg>
              <Tabs tabs={state.companiesTabs} />
            </Container>
          </Box>
        )}
        <Container padding maxWidth="xl">
          <IsEmailVerifiedAlert isEmailVerified={state.portfolioResponse.user.isEmailVerified} />
          <FilterForNotConnectedCompanies state={state} actions={actions} />
          <Box display={"block"}>
            {state.portfolioResponse.companyItems.length > 0 && hasUserCompaniesInPortfolio ? (
              <>
                {state.companiesTab !== "not-connected" && (
                  <SearchAndToolBarCompanies
                    state={state}
                    actions={actions}
                    hasMultipleCompanies={hasMultipleCompanies}
                    includePrimaryActions={
                      state.portfolio.type === "portfolio-instance" &&
                      state.companiesTab !== "ar-status" &&
                      state.companiesTab !== "beneficial-owners"
                    }
                  />
                )}
                <CompaniesInPortfolioTable state={state} actions={actions} />
              </>
            ) : state.portfolio.type !== "portfolio-instance" ? (
              <CompaniesInPortfolioTable state={state} actions={actions} />
            ) : (
              <EmptyPortfolioView
                portfolioName={state.portfolioResponse.portfolio?.name}
                openAddCompanyDialog={actions.addCompanyToPortfolioActions.openAddCompanyDialog}
                openPortfolioSettings={actions.openPortfolioSettings}
                confirmDissolvePortfolio={actions.confirmDissolvePortfolio}
                openTransferOwnershipDialog={
                  isPortfolioOwner(state.portfolioResponse)
                    ? actions.openTransferOwnershipDialog
                    : undefined
                }
              />
            )}
            {state.isAddCompanyDialogOpen && (
              <AddCompanyToPortfolioDialog
                state={state}
                actions={actions.addCompanyToPortfolioActions}
              />
            )}
            {state.isNewProjectDialogOpen && hasMultipleCompanies && (
              <NewProjectDialog
                state={state}
                actions={actions.newProjectActions}
                companyToPortfolioActions={actions.addCompanyToPortfolioActions}
              />
            )}
            {state.signShareholderRegisters && (
              <SignShareholderRegistersDialog
                state={state}
                form={state.signShareholderRegisters}
                actions={actions.signShareholderRegistersActions}
                companyToPortfolioActions={actions.addCompanyToPortfolioActions}
              />
            )}
            {state.removeCompanyFromPortfolio && (
              <RemoveCompanyDialog state={state} actions={actions} />
            )}
            {state.moveToPortfolio && (
              <MoveCompanyToPortfolioDialog state={state} actions={actions} />
            )}
            {state.openInvitationRequest && (
              <PendingInvitationDetailsDialog
                invitationRequest={state.openInvitationRequest}
                onClose={actions.closePendingInvitationDialog}
              />
            )}
            {state.showTransferOwnershipDialog && (
              <TransferOwnershipDialog state={state} actions={actions} />
            )}
          </Box>
        </Container>
      </>
    );
  },
);

export const CompaniesInPortfolioTable = observer(
  ({ state, actions }: { state: ReadyState; actions: PortfolioPageActions }) => {
    if (
      state.portfolioResponse.userCompanies.length === 0 &&
      filteredUserCompanies(state).length === 0 &&
      state.companiesTab === "all"
    ) {
      return (
        <AvailableCompaniesEmptyState
          onAddNorwegianCompany={actions.companySearchActions.openNorwegianCompanySearch}
          onAddSwedishCompany={actions.companySearchActions.openSwedishCompanySearch}
        />
      );
    } else if (
      state.portfolioResponse.userCompanies.length === 0 &&
      filteredUserCompanies(state).length === 0 &&
      state.companiesTab === "starred" &&
      state.portfolioSearchModel.isEmpty
    ) {
      return <StarredCompaniesEmptyState />;
    } else if (
      state.notConnected.companies.length === 0 &&
      state.companiesTab === "not-connected" &&
      state.portfolioSearchModel.isEmpty
    ) {
      return (
        <NotConnectedCompaniesEmptyState
          onAddNorwegianCompany={actions.companySearchActions.openNorwegianCompanySearch}
          onAddSwedishCompany={actions.companySearchActions.openSwedishCompanySearch}
        />
      );
    }
    return (
      <CompanyTable
        data={
          state.companiesTab === "not-connected"
            ? state.notConnected.companies
            : filteredUserCompanies(state)
        }
        portfolioActions={actions.portfolioActions}
        isRenderStar={actions.portfolioActions.isRenderStar}
        onSelect={
          state.companiesTab === "beneficial-owners"
            ? actions.portfolioActions.openBeneficialOwnersPage
            : state.companiesTab === "ar-status"
              ? actions.portfolioActions.openARPage
              : actions.portfolioActions.onSelect
        }
        activeTab={state.companiesTab}
        onClickStar={actions.portfolioActions.onClickStar}
        userSettings={state.userSettings}
        isNoActiveSubscription={(orgNo) => isNoActiveSubscription(state, orgNo)}
        hoveredTransactionData={state.hoveredTransaction}
      />
    );
  },
);

export function AvailableCompaniesEmptyState({
  onAddNorwegianCompany,
  onAddSwedishCompany,
}: {
  onAddNorwegianCompany: () => void;
  onAddSwedishCompany: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Box padding={"lg"}>
      <EmptyState
        illustration={<Icon name={"briefcase"} duotone size={"mega"} />}
        title={t("portfolio.empty-active-companies.title")}
        description={t("portfolio.empty-active-companies.description")}
        actions={[
          <Button
            key={"add-norwegian-company"}
            onClick={onAddNorwegianCompany}
            label={t("portfolio.empty-active-companies.add-norwegian-company")}
            variant={"secondary"}
          />,
          <Button
            key={"add-swedish-company"}
            onClick={onAddSwedishCompany}
            label={t("portfolio.empty-active-companies.add-swedish-company")}
            variant={"secondary"}
          />,
        ]}
      />
      <BottomSpacer />
    </Box>
  );
}

function NotConnectedCompaniesEmptyState({
  onAddNorwegianCompany,
  onAddSwedishCompany,
}: {
  onAddNorwegianCompany: () => void;
  onAddSwedishCompany: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Box padding={"lg"}>
      <EmptyState
        illustration={<Icon name={"plugs"} duotone size={"mega"} />}
        title={t("portfolio.empty-not-connected-companies.title")}
        description={t("portfolio.empty-not-connected-companies.description")}
        actions={[
          <Button
            key={"add-norwegian-company"}
            onClick={onAddNorwegianCompany}
            label={t("portfolio.empty-active-companies.add-norwegian-company")}
            variant={"secondary"}
          />,
          <Button
            key={"add-swedish-company"}
            onClick={onAddSwedishCompany}
            label={t("portfolio.empty-active-companies.add-swedish-company")}
            variant={"secondary"}
          />,
        ]}
      />
      <BottomSpacer />
    </Box>
  );
}

function StarredCompaniesEmptyState() {
  const { t } = useTranslation();
  return (
    <Box padding={"lg"}>
      <EmptyState
        illustration={<Icon name={"star"} duotone size={"mega"} />}
        title={t("portfolio.empty-starred-companies.title")}
        description={t("portfolio.empty-starred-companies.description")}
      />
      <BottomSpacer />
    </Box>
  );
}

const SearchAndToolBarCompanies = observer(
  ({
    state,
    actions,
    hasMultipleCompanies,
    includePrimaryActions,
  }: {
    state: ReadyState;
    hasMultipleCompanies: boolean;
    actions: PortfolioPageActions;
    includePrimaryActions: boolean;
  }) => {
    const { t } = useTranslation();

    return (
      <Box justifyContent="space-between" marginBottom vertical={isMobileView()}>
        <Box gap={"md"}>
          <MTextInput
            placeholder={
              state.portfolioResponse.portfolio
                ? t("search_in", { name: state.portfolioResponse.portfolio.name })
                : t("search")
            }
            icon={"magnifying-glass"}
            model={state.portfolioSearchModel}
            onChange={(e) => {
              state.portfolioSearchModel.setInputValue(e);
              actions.portfolioActions.filterWithText();
            }}
            autofocus
            type={"search"}
          />
          <Box gap={"md"}>
            <Select
              {...prepareCompaniesWithSubscriptionStatus(
                state.companySubscription,
                actions.setCompaniesWithSubscriptionStatus,
              )}
            />
            {state.companySubscription !== "all" && (
              <LinkButton
                label={t("showAll")}
                icon={"arrow-counter-clockwise"}
                onClick={() => actions.setCompaniesWithSubscriptionStatus("all")}
              />
            )}
          </Box>
          {state.companiesTab === "all" && (
            <Box>
              {state.customRoleFilter && <MSelect model={state.customRoleFilter} />}
              {state.customRoleFilter && state.customRoleFilter.value !== null && (
                <LinkButton
                  label={t("showAll")}
                  icon={"arrow-counter-clockwise"}
                  onClick={actions.resetCustomRoleFilter}
                />
              )}
            </Box>
          )}
          {state.companiesTab === "ar-status" && state.arStatusFilter && (
            <Box>
              <MSelect model={state.arStatusFilter} />
            </Box>
          )}
        </Box>
        <Box gap={isMobileView() ? "sm" : "md"} vertical={isMobileView()}>
          {includePrimaryActions && (
            <>
              <Button
                label={t("portfolio.invite-admin")}
                icon={"user-plus"}
                variant={"secondary"}
                onClick={actions.openInviteAdministratorDialog}
              />
              <ManagePortfolioMenu
                state={state}
                actions={actions}
                hasMultipleCompanies={hasMultipleCompanies}
              />
            </>
          )}
        </Box>
        {state.companiesTab === "ar-status" && (
          <Box alignItems={"center"}>
            <Markdown value={t("portfolio.ar.documentation")} enableLinks disableLastMargin />{" "}
            <Icon name="arrow-square-out" color={"info"} />
          </Box>
        )}
        {state.companiesTab === "beneficial-owners" && (
          <Box alignItems={"center"}>
            <Markdown
              value={t("portfolio.beneficial-owners.documentation")}
              enableLinks
              disableLastMargin
            />{" "}
            <Icon name="arrow-square-out" color={"info"} />
          </Box>
        )}
      </Box>
    );
  },
);

const ManagePortfolioMenu = observer(
  ({
    state,
    actions,
    hasMultipleCompanies,
  }: {
    state: ReadyState;
    actions: PortfolioPageActions;
    hasMultipleCompanies: boolean;
  }) => {
    const { t } = useTranslation();
    const decisionsByCategory = getDecisionsByCategory({
      isBatchProject: true,
      checkedDecisions: state.checkedDecisions,
      paidSubscription: {
        ...allFeaturesIncluded,
        organisationNumber: "",
        subscriptionStatus: SubscriptionStatus.ACTIVE,
        subscriptionType: SubscriptionType.PRO,
        isActive: true,
      },
    });
    return (
      <DropdownMenu
        button={{
          type: "button",
          variant: "secondary",
          label: t("portfolio.options.menu.manage-companies"),
        }}
        items={[
          hasMultipleCompanies && {
            type: "label",
            label: t("portfolio.options.menu.start-portfolio-project"),
          },

          ...decisionsByCategory.flatMap((category) =>
            category.decisions.map<DropdownMenuItemType | null>((type) =>
              hasMultipleCompanies
                ? {
                    type: "item",
                    label: <DecisionLabel decisionType={type.decisionType} />,
                    icon: "sparkle",
                    onSelect: () => {
                      actions.openCreateProjectDialog(type.decisionType);
                    },
                  }
                : null,
            ),
          ),
          { type: "separator" },
          { type: "label", label: t("portfolio.options.menu.expand-portfolio") },
          {
            type: "item",
            label: t("portfolio.welcome.add-company"),
            icon: "plus",
            onSelect: () => {
              actions.addCompanyToPortfolioActions.openAddCompanyDialog();
            },
          },
          {
            type: "item",
            label: t("portfolio.invite-admin"),
            icon: "user-plus",
            onSelect: () => {
              actions.openInviteAdministratorDialog();
            },
          },
          {
            type: "item",
            label: t("portfolio.export-shareholder-registers"),
            icon: "download",
            onSelect: () => {
              actions.openExportShareholdersDialog();
            },
          },
          {
            type: "item",
            label: t("portfolio.sign-shareholder-registers"),
            icon: "signature",
            tag: { label: { key: "dbot.new" }, color: "info", compact: true },
            onSelect: () => {
              actions.signShareholderRegistersActions.openDialog();
            },
          },
          isPortfolioOwner(state.portfolioResponse) && {
            type: "item",
            label: t("portfolio.transfer-ownership"),
            icon: "user-switch",
            onSelect: () => actions.openTransferOwnershipDialog(),
          },
          {
            type: "item",
            label: t("portfolio.options.menu.rename-portfolio"),
            icon: "edit",
            onSelect: () => {
              actions.openPortfolioSettings();
            },
          },
          {
            type: "item",
            label: t("portfolio.dissolve-portfolio"),
            icon: "trash",
            color: "error",
            onSelect: () => {
              actions.confirmDissolvePortfolio();
            },
          },
        ]}
      />
    );
  },
);

const NewProjectDialog = observer(
  ({
    state,
    actions,
    companyToPortfolioActions,
  }: {
    state: ReadyState;
    actions: NewProjectActions;
    companyToPortfolioActions: AddCompanyToPortfolioActions;
  }) => {
    const { t } = useTranslation();
    const decisionsByCategory = getDecisionsByCategory({
      isBatchProject: true,
      checkedDecisions: state.checkedDecisions,
      paidSubscription: {
        ...allFeaturesIncluded,
        organisationNumber: "",
        subscriptionStatus: SubscriptionStatus.ACTIVE,
        subscriptionType: SubscriptionType.PRO,
        isActive: true,
      },
    });

    const dbotCompanies = toCompaniesWithDbotAccess(state);
    const selectedAdminCompanies = dbotCompanies.filter(
      (c) =>
        c.accessType === AccessType.ADMIN && state.selectedCompanies.includes(c.organisationNumber),
    );
    const primaryCompany =
      selectedAdminCompanies.find((c) => c.organisationNumber === state.primaryCompanyOrgNo) ??
      selectedAdminCompanies[0];

    return (
      <Dialog
        title={
          state.portfolioResponse.portfolio?.name
            ? t("decisions.what-to-do-in", { in: state.portfolioResponse.portfolio?.name })
            : t("decisions.what-to-do")
        }
        submitLabel={
          primaryCompany
            ? t("portfolio.project.continue", {
                count: selectedAdminCompanies.length,
                company: primaryCompany.name,
              })
            : t("continue")
        }
        isSubmitDisabled={state.checkedDecisions.length === 0}
        isSubmitting={isPending(state.createProjectResult)}
        onSubmit={actions.createBatchProject}
        onCancel={actions.closeNewProjectDialog}
        height="lg"
      >
        <DecisionsAndCompaniesList
          decisions={decisionsByCategory}
          toggleDecision={actions.toggleDecision}
          companies={toCompaniesWithDbotAccess(state)}
          action={companyToPortfolioActions}
          companySearchFieldModel={state.companySearchFieldModel}
          selected={state.selectedCompanies.length}
          showOnlySelected={state.showOnlySelected}
          primaryCompanyOrgNo={state.primaryCompanyOrgNo}
        />
        {isFailure(state.createProjectResult) && (
          <Alert type="error" message={state.createProjectResult.error} />
        )}
      </Dialog>
    );
  },
);

const DecisionsAndCompaniesList = observer(
  ({
    decisions,
    companies,
    toggleDecision,
    action,
    companySearchFieldModel,
    selected,
    showOnlySelected,
    primaryCompanyOrgNo,
  }: {
    decisions: DecisionsByCategory[];
    companies: PortfolioCompany[];
    toggleDecision: (decision: DecisionType) => void;
    action?: AddCompanyToPortfolioActions;
    companySearchFieldModel?: StringFieldModel;
    selected: number;
    showOnlySelected?: boolean;
    primaryCompanyOrgNo?: string;
  }) => {
    const { t } = useTranslation();
    return (
      <Grid cols={2} className="flex-1 min-h-0">
        <GridItem>
          <VBox>
            <strong>{t("dbot.matters")}</strong>
            {decisions.map((decisions) =>
              decisions.decisions.map((decisionType) => (
                <div key={decisionType.decisionType}>
                  <Checkbox
                    checked={decisionType.isSelected}
                    label={<DecisionLabel decisionType={decisionType.decisionType} />}
                    onChange={() => toggleDecision(decisionType.decisionType)}
                  />
                </div>
              )),
            )}
          </VBox>
        </GridItem>
        <CompaniesPickerList
          companies={companies}
          action={action}
          companySearchFieldModel={companySearchFieldModel}
          selected={selected}
          showOnlySelected={showOnlySelected}
          primaryCompanyOrgNo={primaryCompanyOrgNo}
        />
      </Grid>
    );
  },
);

const SignShareholderRegistersDialog = observer(
  ({
    state,
    form,
    actions,
    companyToPortfolioActions,
  }: {
    state: ReadyState;
    form: SignShareholderRegistersForm;
    actions: SignShareholderRegistersActions;
    companyToPortfolioActions: AddCompanyToPortfolioActions;
  }) => {
    const { t } = useTranslation();
    const adminCompanies = toCompaniesWithAdminDbotAccess(state);
    const selectedCompanies = adminCompanies.filter((c) =>
      state.selectedCompanies.includes(c.organisationNumber),
    );
    const primaryCompany =
      selectedCompanies.find((c) => c.organisationNumber === state.primaryCompanyOrgNo) ??
      selectedCompanies[0];
    const hasMultipleCompanies = adminCompanies.length > 1;
    return (
      <Dialog
        title={t("portfolio.sign-shareholder-registers")}
        submitLabel={
          primaryCompany
            ? t("portfolio.project.continue", {
                count: selectedCompanies.length,
                company: primaryCompany.name,
              })
            : t("continue")
        }
        isSubmitDisabled={!form.date.isValid || selectedCompanies.length === 0}
        isSubmitting={isPending(state.createProjectResult)}
        onSubmit={() => actions.createProject(form)}
        onCancel={actions.closeDialog}
        maxWidth={hasMultipleCompanies ? "md" : "sm"}
        height={hasMultipleCompanies ? "lg" : "auto"}
      >
        <Grid cols={hasMultipleCompanies ? 2 : 1} gap={"xl"} className="flex-1 min-h-0">
          <GridItem>
            <VBox gap={"xl"}>
              <div>{t("portfolio.sign-shareholder-registers.description")}</div>
              <MSelect label={t("shareholders.exportContent.label")} model={form.format} />
              <VBox>
                <label htmlFor={"signRegistersDate"}>
                  <Box>
                    {t("shareholders.exportContent.per-date")}
                    <InfoButton infoText={t("shareholders.exportContent.info")} />
                  </Box>
                </label>
                <MDateInput id={"signRegistersDate"} model={form.date} />
              </VBox>
              <VBox>
                <div>{t("shareholders.exportLanguage.select")}</div>
                <MRadioButtonGroup
                  name={"signRegistersLanguage"}
                  model={form.language}
                  horizontal
                />
              </VBox>
            </VBox>
          </GridItem>
          {hasMultipleCompanies && (
            <CompaniesPickerList
              companies={adminCompanies}
              action={companyToPortfolioActions}
              companySearchFieldModel={state.companySearchFieldModel}
              selected={state.selectedCompanies.length}
              showOnlySelected={state.showOnlySelected}
              primaryCompanyOrgNo={state.primaryCompanyOrgNo}
            />
          )}
        </Grid>
        {isFailure(state.createProjectResult) && (
          <Alert type="error" message={state.createProjectResult.error} />
        )}
      </Dialog>
    );
  },
);

const CompaniesPickerList = observer(
  ({
    companies,
    action,
    companySearchFieldModel,
    selected,
    showOnlySelected,
    primaryCompanyOrgNo,
  }: {
    companies: PortfolioCompany[];
    action?: AddCompanyToPortfolioActions;
    companySearchFieldModel?: StringFieldModel;
    selected: number;
    showOnlySelected?: boolean;
    primaryCompanyOrgNo?: string;
  }) => {
    const { t } = useTranslation();
    const highlightWords = companySearchFieldModel?.inputValue
      ? tokenizeFilterString(companySearchFieldModel.inputValue)
      : [];
    return (
      <GridItem className="flex flex-col min-h-0">
        <VBox className="flex-1 min-h-0">
          <strong>{t("dbot.companies")}</strong>
          {companySearchFieldModel && (
            <div>
              <MTextInput
                model={companySearchFieldModel}
                icon={"magnifying-glass"}
                placeholder={t("portfolioContacts.nameFilterPlaceholder")}
                type={"search"}
              />
            </div>
          )}
          <Box alignItems={"center"} justifyContent={"space-between"} marginBottom={"md"}>
            <Box gap={"md"}>
              <LinkButton label={t("select-all")} onClick={action?.toggleAllCompanies} />
              <LinkButton label={t("select-none")} onClick={action?.deselectAllCompanies} />
            </Box>
            {action?.filterSelectedCompanies && (
              <LinkButton
                label={
                  showOnlySelected
                    ? t("showAll")
                    : t("portfolio.company.portfolio-project.show-selected-companies", {
                        count: selected,
                      })
                }
                onClick={action?.filterSelectedCompanies}
              />
            )}
          </Box>
          <Box justifyContent="space-between">
            <div>{t("portfolio.company")}</div>
            <Box>
              <div>{t("portfolio.primary")}</div>
              <InfoButton icon={"info"} infoText={t("portfolio.primary.info-text")} />
            </Box>
          </Box>
          <VBox flex={"auto"} overflow={"auto"} padding={"sm"}>
            {filterCompaniesForAddDialog(
              companies,
              companySearchFieldModel?.inputValue,
              action,
              primaryCompanyOrgNo,
            )
              .filter((c) => !showOnlySelected || c.isSelected)
              .map(({ name, organisationNumber, isSelected, isPrimary }) => (
                <Box
                  key={organisationNumber}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Checkbox
                    label={<HighlightText value={name} words={highlightWords} />}
                    value={organisationNumber}
                    checked={isSelected}
                    onChange={() => action?.toggleCompanyInAddDialog(organisationNumber)}
                  />
                  <RadioButton
                    checked={isPrimary}
                    disabled={!isSelected}
                    onChange={() => action?.setPrimaryCompany(organisationNumber)}
                  />
                </Box>
              ))}
          </VBox>
        </VBox>
      </GridItem>
    );
  },
);

const AddCompanyToPortfolioDialog = observer(
  ({ state, actions }: { state: ReadyState; actions: AddCompanyToPortfolioActions }) => {
    const { t } = useTranslation();
    const isSubmitDisabled =
      state.selectedCompanies.length === 0 || state.availableCompanies.length === 0;
    return state.portfolioResponse.portfolio ? (
      <Dialog
        title={t("portfolio.add-companies", { name: state.portfolioResponse.portfolio.name })}
        onCancel={actions.closeAddCompanyDialog}
        onSubmit={actions.saveCompaniesInPortfolio}
        isSubmitting={isPending(state.addCompaniesResult)}
        submitResult={state.addCompaniesResult}
        isSubmitDisabled={isSubmitDisabled}
      >
        <VBox gap={"sm"}>
          <div>{t("portfolio.add-companies.description")}</div>
          {state.availableCompanies
            .filter((c) => isBlank(c.portfolioId))
            .map(({ name, organisationNumber }) => (
              <Checkbox
                checked={actions.isCompanySelected(organisationNumber)}
                label={name}
                key={organisationNumber}
                value={organisationNumber}
                onChange={() => actions.toggleCompanyInAddDialog(organisationNumber)}
                disabled={isPending(state.addCompaniesResult)}
              />
            ))}
          {state.availableCompanies
            .filter((c) => isNotBlank(c.portfolioId))
            .map(({ name, organisationNumber }) => (
              <Checkbox label={name} key={organisationNumber} value={organisationNumber} disabled />
            ))}
        </VBox>
      </Dialog>
    ) : null;
  },
);

const RemoveCompanyDialog = observer(
  ({ state, actions }: { state: ReadyState; actions: PortfolioPageActions }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t("portfolio.remove-from-portfolio.title", {
          company: state.removeCompanyFromPortfolio?.name,
          portfolio: state.portfolioResponse?.portfolio?.name,
        })}
        onCancel={actions.portfolioActions.closeRemoveCompanyDialog}
        onSubmit={actions.portfolioActions.removeFromPortfolio}
        isSubmitting={isPending(state.removeFromPortfolioResult)}
        submitResult={state.removeFromPortfolioResult}
        submitLabel={t("continue")}
      >
        <div>
          {t("portfolio.remove-from-portfolio.description", {
            company: state.removeCompanyFromPortfolio?.name,
          })}
          <br />
          <br />
        </div>
        <div>{t("sure-you-want-to-proceed")}</div>
      </Dialog>
    );
  },
);

const MoveCompanyToPortfolioDialog = observer(
  ({ state, actions }: { state: ReadyState; actions: PortfolioPageActions }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t("portfolio.move-company-dialog.title", {
          company: state.moveToPortfolio?.name,
          portfolio: state.portfolioResponse?.portfolio?.name,
        })}
        onCancel={actions.portfolioActions.closeMoveCompanyToPortfolioDialog}
        onSubmit={actions.portfolioActions.moveCompanyToPortfolio}
        isSubmitting={isPending(state.moveToPortfolioResult)}
        submitResult={state.moveToPortfolioResult}
        submitLabel={t("continue")}
      >
        <div>
          <Autocomplete
            disabled={isPending(state.moveToPortfolioResult) || isOk(state.moveToPortfolioResult)}
            label={t("portfolio.move-company-dialog.label")}
            placeholder={t("archive.move-dialog.destination-folder.placeholder")}
            items={state.portfolioResponse.portfolios
              .filter((p) => p.id !== state.portfolioResponse.portfolio?.id)
              .toSorted((a, b) => a.name.localeCompare(b.name))}
            idFn={(entry) => entry.id}
            labelFn={(entry) => entry.name}
            onSelect={(entry) => actions.portfolioActions.setDestinationPortfolio(entry)}
          />
        </div>
        <br />
        <Alert type={"info"} message={t("portfolio.move-company-dialog.only-your-portfolios")} />
      </Dialog>
    );
  },
);

export const AskForPermissionActions: FC<{
  result?: Result;
  closeAction: () => void;
  submitAction: (autoonboard: boolean) => void;
  onboardingCandidatesResult?: Result<OnboardingCandidateResponse>;
  isBatch?: boolean;
  disableSubmit?: boolean;
}> = observer(
  ({ result, closeAction, submitAction, onboardingCandidatesResult, isBatch, disableSubmit }) => {
    const { t } = useTranslation();

    return (
      <Box justifyContent={"space-between"} alignItems={"baseline"}>
        <div>
          {!isProductionEnvironment() && (
            <div className={"subtle"}>Demo only: Shift click for instant onboarding</div>
          )}
        </div>
        <Box className={"flex-row-reverse"}>
          <Button
            variant={"primary"}
            label={t("askForPermissionDialog.sendRequest")}
            onClick={(e) => submitAction(e.shiftKey && !isProductionEnvironment())}
            disabled={
              isPending(result) || (!isOk(onboardingCandidatesResult) && !isBatch) || disableSubmit
            }
            loading={isPending(result)}
          />
          <Button variant={"secondary"} label={t("close")} onClick={closeAction} />
        </Box>
      </Box>
    );
  },
);

export const NorwegianCompanySearchDialog: FC<{
  state: CompanySearchDialogState;
  actions: CompanySearchActions;
}> = observer(({ state, actions }) => {
  const { t } = useTranslation();
  const locale = useLanguage();

  return (
    <Dialog
      title={t(companySearchDialogTitle(state), { company: state.selectedCompany?.name })}
      isSubmitting={isPending(state.askForPermissionResult)}
      fullscreen={isMobileView()}
      onCancel={actions.closeNorwegianCompanySearch}
      actions={
        <AskForPermissionActions
          onboardingCandidatesResult={state.onboardingCandidatesResult}
          result={state.askForPermissionResult}
          closeAction={actions.closeNorwegianCompanySearch}
          submitAction={actions.sendAskForPermissionRequest}
          disableSubmit={isDisabledOptionSelected(state)}
        />
      }
      maxWidth={"md"}
    >
      {!isOk(state.askForPermissionResult) && !state.selectedCompany && (
        <VBox gap={"md"}>
          <p>{t("registerCompany.addCompany.body")}</p>
          <Autocomplete
            placeholder={t("registerCompany.searchLabel")}
            idFn={(i) => i.organizationnumber}
            items={isOk(state.searchResult) ? state.searchResult.data : []}
            inputValue={state.companyNameSearchField.inputValue}
            labelFn={(company) => company.name}
            onSelect={(company) => actions.selectCompany(company)}
            onInputValueChange={(query) => actions.searchForCompany(query)}
            onClear={() => state.companyNameSearchField.clear()}
            disabled={state.disableSearch}
            loading={isPending(state.searchResult)}
          />
        </VBox>
      )}
      {state.selectedCompany && (
        <VBox gap={"md"}>
          {state.sourceOrgNoName && state.ownershipInPercent && (
            <div>
              {t("registerCompany.company.addCompany.ownsdescription", {
                source: state.sourceOrgNoName,
                percent: formatPercent(locale, state.ownershipInPercent),
                target: state.selectedCompany.name,
              })}
            </div>
          )}
          <RequestAccessToCompanyForm
            companyName={state.selectedCompany.name}
            onboardingCandidatesResult={state.onboardingCandidatesResult}
            askForPermissionResult={state.askForPermissionResult}
            accessTypeModel={state.accessTypeModel}
            personalNoteModel={state.personalNoteModel}
            receiverEmailAddress={state.receiverEmailAddress}
            companyRegisterKey={getCompanyRegisterKey(state.selectedCompany)}
          />
        </VBox>
      )}
    </Dialog>
  );
});

const RequestAccessToExistingCompanyForm = observer(function RequestAccessToExistingCompanyForm({
  companyName,
  accessTypeModel,
  personalNoteModel,
  onboardingResponse,
}: {
  companyName: string;
  accessTypeModel?: RadioFieldModel<RequestAccessType | null>;
  personalNoteModel?: StringFieldModel;
  onboardingResponse: OnboardingCandidateResponse;
}) {
  const { t } = useTranslation();
  const isDisabled =
    (accessTypeModel?.value === "shareholder-portal" &&
      !onboardingResponse.isShareholderPortalEnabled) ||
    (accessTypeModel?.value === "board-portal" && !onboardingResponse.isBoardPortalEnabled);
  return (
    <VBox gap={"md"}>
      <Box>{t("registerCompany.requestAccess.text", { companyName })}</Box>
      {accessTypeModel && (
        <MSelect
          autofocus
          label={{ key: "registerCompany.requestAccess.label" }}
          model={accessTypeModel}
          labelFn={(type) =>
            type ? (
              <Box alignItems={"center"}>
                <Icon name={requestAccessTypeIcons[type]} />
                <VBox gap={0}>
                  <Box>{t(requestAccessTypeTitleLabel[type])}</Box>
                  <Box fg={"subtle"} textSize={"sm"}>
                    {t(requestAccessTypeDescriptionLabel[type])}
                  </Box>
                </VBox>
              </Box>
            ) : (
              " "
            )
          }
        />
      )}
      {isDisabled ? (
        <Alert
          message={t(
            accessTypeModel?.value === "shareholder-portal"
              ? "registerCompany.shareholderPortal.notEnabledWarning"
              : "registerCompany.boardPortal.notEnabledWarning",
            { companyName: onboardingResponse.companyName },
          )}
          type={"error"}
        />
      ) : (
        accessTypeModel?.value &&
        personalNoteModel && (
          <VBox>
            <MarkdownEditor
              label={t("registerCompany.personalNote.label")}
              value={personalNoteModel.inputValue}
              onChange={(value) => personalNoteModel?.setAndCommitInputValue(value)}
            />
            {personalNoteModel?.error && <Alert message={personalNoteModel.error} type={"error"} />}
          </VBox>
        )
      )}
    </VBox>
  );
});

export const RequestAccessToCompanyForm = observer(function RequestAccessToCompanyForm({
  companyName,
  onboardingCandidatesResult,
  askForPermissionResult,
  accessTypeModel,
  personalNoteModel,
  receiverEmailAddress,
  companyRegisterKey,
}: {
  companyName: string;
  onboardingCandidatesResult?: Result<OnboardingCandidateResponse>;
  askForPermissionResult?: Result;
  accessTypeModel?: RadioFieldModel<RequestAccessType | null>;
  personalNoteModel?: StringFieldModel;
  receiverEmailAddress: StringFieldModel;
  companyRegisterKey: TMessageKey;
}) {
  return isPending(onboardingCandidatesResult) ? (
    <LoadingIndicator />
  ) : isOk(onboardingCandidatesResult) &&
    onboardingCandidatesResult.data.isCustomer &&
    !isOk(askForPermissionResult) ? (
    <RequestAccessToExistingCompanyForm
      companyName={companyName}
      accessTypeModel={accessTypeModel}
      personalNoteModel={personalNoteModel}
      onboardingResponse={onboardingCandidatesResult.data}
    />
  ) : (
    <AskForPermissionForm
      company={companyName}
      askForPermissionResult={askForPermissionResult}
      receiverEmailAddress={receiverEmailAddress}
      onboardingCandidatesResult={onboardingCandidatesResult}
      companyRegisterKey={companyRegisterKey}
    />
  );
});

export const SwedishCompanySearchDialog: FC<{
  state: CompanySearchDialogState;
  actions: CompanySearchActions;
}> = observer(({ state, actions }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("portfolio.search-bolagsverket.title")}
      actions={
        <AskForPermissionActions
          result={state.askForPermissionResult}
          onboardingCandidatesResult={state.onboardingCandidatesResult}
          closeAction={actions.closeSwedishCompanySearch}
          submitAction={actions.sendAskForPermissionRequest}
        />
      }
      isSubmitting={isPending(state.askForPermissionResult)}
      cancelLabel={t("close")}
      fullscreen={isMobileView()}
      maxWidth={"md"}
    >
      {!isOk(state.askForPermissionResult) && (
        <VBox>
          <Box paddingBottom={"lg"}>{t("portfolio.search-bolagsverket.description")}</Box>
          <Box vertical={isMobileView()}>
            <MTextInput
              placeholder={t("portfolio.search-bolagsverket.search-placeholder")}
              model={state.searchField}
            />
            <div>
              <Button
                variant={"secondary"}
                type={"submit"}
                label={t("search")}
                loading={isPending(state.searchResult)}
                onClick={actions.searchSwedishBusinessRegister}
              />
            </div>
          </Box>
        </VBox>
      )}
      <VBox>
        {isOk(state.searchResult) && state.isRenderSearchResult && (
          <>
            <VBox padding>
              {t("portfolio.search-bolagsverket.result-count", {
                count: state.searchResult.data.length,
              })}
            </VBox>

            {state.searchResult.data.map((it) => {
              return (
                <SearchResultTableRow
                  key={it.name + it.organizationnumber}
                  company={it}
                  onSelect={() => actions.selectSwedishCompany(it)}
                />
              );
            })}
          </>
        )}
        {state.tooManyHits && <div>{t("portfolio.search-bolagsverket.too-many-hits")}</div>}
        {isOk(state.searchResult) && state.searchResult.data.length === 0 && (
          <div>
            {t("portfolio.search-bolagsverket.no-hits", {
              searchQuery: state.searchField.inputValue,
            })}
          </div>
        )}
        {state.selectedCompany && (
          <AskForPermissionForm
            company={state.selectedCompany.name}
            askForPermissionResult={state.askForPermissionResult}
            receiverEmailAddress={state.receiverEmailAddress}
            onboardingCandidatesResult={state.onboardingCandidatesResult}
            companyRegisterKey={getCompanyRegisterKey(state.selectedCompany)}
          />
        )}
      </VBox>
    </Dialog>
  );
});

function getCompanyRegisterKey(company?: SuggestedCompany) {
  if (company?.sourceCountry === "SE") {
    return "askForPermissionDialog.bolagsverket";
  } else {
    return "askForPermissionDialog.brreg";
  }
}

export const PortfolioCompaniesView = observer(
  ({
    state,
    alertStore,
    showSigningCompleteDialog,
    closeSigningCompleteDialog,
    companyName,
    portfolio,
  }: {
    state: PreparedPageState;
    alertStore: AlertMessageStore;
    showSigningCompleteDialog: boolean;
    closeSigningCompleteDialog: () => void;
    companyName: string;
    portfolio: SelectedPortfolioOption;
  }) => {
    const renderBody = () => {
      switch (true) {
        case isPreparedPageState(state, "invalid-portfolioid"):
          return <InvalidPortfolioView />;
        case isPreparedPageState(state, "loading-portfolio"):
          return <LoadingIndicator fullscreen />;
        case isPreparedPageState(state, "ready"):
          return <PortfolioCompaniesReadyView {...state} />;
      }
    };
    const appBar = useAppBar({
      portfolioHeader: "companies",
      isPortfolioPage: true,
      selectedPortfolioOption: portfolio,
      openPortfolioSettings:
        isPreparedPageState(state, "ready") &&
        state.state.activeHeader === "follow-up" &&
        state.state.portfolio.type === "portfolio-instance"
          ? state.actions.openPortfolioSettings
          : undefined,
    });

    const { t, lang } = useTranslation();
    const userPrefs = useUserPrefs();
    const subHeaderElement: ReactElement | undefined =
      isPreparedPageState(state, "ready") && state.state.portfolio.type !== "portfolio-instance" ? (
        <Box bg>
          <SigningCompleteDialog
            companyName={companyName}
            showSigningCompleteDialog={showSigningCompleteDialog}
            closeSigningCompleteDialog={closeSigningCompleteDialog}
          />
          <VBox maxWidth="xl" container marginTop="lg">
            <Box justifyContent={"space-between"} alignItems={"center"}>
              <h4 data-testid={"portfolio-header"}>{t("header.portfolio")}</h4>
              <Box gap="xl" vertical={isMobileView()}>
                <DropdownMenu
                  button={{
                    type: "link",
                    icon: "plus",
                    label: t("portfolio.welcome.add-company"),
                  }}
                  items={[
                    {
                      type: "item",
                      label: t("norway"),
                      onSelect: state.actions.companySearchActions.openNorwegianCompanySearch,
                    },
                    !isProductionEnvironment() && {
                      type: "item",
                      label: t("portfolio.add-company.norwegian-ans-da"),
                      onSelect: state.actions.companySearchActions.openNorwegianCompanySearch,
                    },
                    {
                      type: "item",
                      label: t("sweden"),
                      onSelect: state.actions.companySearchActions.openSwedishCompanySearch,
                    },
                  ]}
                />
                {!isMobileView() && (
                  <PortfolioActionsDropdown state={state.state} actions={state.actions} />
                )}
              </Box>
            </Box>
            <QuickFilterBar
              {...prepPortfolioQuickSelector({
                filters: state.state.quickFilters,
                customRoleFilter: state.state.customRoleFilter,
                companiesInPortfolio: state.state.userCompaniesInPortfolio,
                userPrefs,
                language: lang,
              })}
            />
            <div>
              <Tabs tabs={state.state.companiesTabs} />
            </div>
          </VBox>
        </Box>
      ) : undefined;

    return (
      <FullScreenLayout
        alertStore={alertStore}
        appBar={<AppBar data={appBar} disableDropShadow subHeaderElement={subHeaderElement} />}
        centerPanel={renderBody()}
        disableAppBarShadow
      />
    );
  },
);

export const PortfolioCompaniesReadyView = observer(
  ({ state, actions }: { state: ReadyState; actions: PortfolioPageActions }) => {
    return (
      <>
        {state.confirmDissolvePortfolio && (
          <ConfirmDissolveDialog
            state={state}
            actions={actions}
            contactsAdminsUrl={extractPortfolioContactsAdminUrl(state.portfolio)}
          />
        )}
        {!state.confirmDissolvePortfolio && state.isPortfolioSettingsOpen && (
          <PortfolioSettingsDialog
            state={state}
            actions={actions}
            contactsAdminsUrl={extractPortfolioContactsAdminUrl(state.portfolio)}
            canDissolvePortfolio={isPortfolioOwner(state.portfolioResponse)}
          />
        )}
        {state.norwegianCompanySearchDialogState && (
          <NorwegianCompanySearchDialog
            state={state.norwegianCompanySearchDialogState}
            actions={actions.companySearchActions}
          />
        )}
        {state.swedishCompanySearchDialogState && (
          <SwedishCompanySearchDialog
            state={state.swedishCompanySearchDialogState}
            actions={actions.companySearchActions}
          />
        )}
        <CompaniesView state={state} actions={actions} />
        {hasAdminAccess(state.portfolioResponse) && state.userCompaniesInPortfolio.length > 0 && (
          <ContactFooter />
        )}
      </>
    );
  },
);

export const PendingInvitationDetailsDialog: FC<{
  invitationRequest: InvitationResponse;
  onClose: () => void;
}> = observer(({ invitationRequest, onClose }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("pendingInvitationDialog.title")}
      cancelLabel={t("close")}
      onCancel={onClose}
      isSubmitDisabled={false}
    >
      {t("pendingInvitationDialog.description", {
        date: { type: "date", value: invitationRequest.created },
      })}
    </Dialog>
  );
});

export const FilterForNotConnectedCompanies: FC<{
  state: ReadyState;
  actions: PortfolioPageActions;
}> = observer(({ state, actions }) => {
  const { t } = useTranslation();
  return state.companiesTab === "not-connected" ? (
    <>
      <Box justifyContent={"space-between"}>
        <Box>
          <Box>
            <MTextInput
              placeholder={t("search")}
              icon={"magnifying-glass"}
              model={state.portfolioSearchModel}
              onChange={(e) => {
                state.portfolioSearchModel.setAndCommitInputValue(e);
                actions.portfolioActions.filterWithText();
              }}
            />
          </Box>
          <Box>
            <MSelect
              model={state.notConnected.rolesFilter}
              onSelect={actions.notConnected.filterWithRole}
            />
          </Box>
        </Box>
        {!isProductionEnvironment() && (
          <Button
            variant={"secondary"}
            icon="plugs"
            label={t("connect-all", {
              count: state.notConnected.companies.filter((c) => c.accessType !== AccessType.DRAFT)
                .length,
            })}
            size={"sm"}
            disabled={
              state.portfolioResponse.subsidiaries.length === 0 ||
              state.notConnected.companies.length === 0
            }
            onClick={actions.notConnected.toggleConnectAllDialog}
          />
        )}
      </Box>
      {state.notConnected.showConnectAllDialog && (
        <ConnectAllDialog state={state.notConnected} actions={actions.notConnected} />
      )}
    </>
  ) : null;
});

const ConnectAllDialog = observer(
  ({
    state,
    actions,
  }: {
    state: NotConnectedTabState;
    actions: ActionsForNotConnectedCompanies;
  }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t("portfolio.connect-all.add-companies")}
        actions={
          <AskForPermissionActions
            isBatch
            result={state.connectAllCompaniesResult}
            submitAction={actions.submitConnectAll}
            closeAction={actions.toggleConnectAllDialog}
          />
        }
      >
        <div>{t("portfolio.connect-all.add-companies-description")}</div>
        <Box marginTop>
          <LinkButton label={t("select-all")} onClick={actions.selectAll} /> /{" "}
          <LinkButton label={t("select-none")} onClick={actions.selectNone} />
        </Box>

        {state.subsidiaries.map((c) => (
          <>
            <Grid key={c.subsidiary.organizationNumber} cols={2} marginTop marginBottom={"sm"}>
              <VBox>
                <Checkbox
                  label={
                    <Box>
                      {c.subsidiary.name}{" "}
                      <div className="subtle">({c.subsidiary.organizationNumber})</div>
                    </Box>
                  }
                  checked={c.checked}
                  onChange={() => actions.toggleCheckedSubsidiary(c)}
                />
                {c.subsidiary.ceoName && (
                  <div>
                    {c.subsidiary.ceoName} ({t("roles.ceo")})
                  </div>
                )}
                {c.subsidiary.chairPersonName && (
                  <div>
                    {c.subsidiary.chairPersonName} ({t("roles.chairperson")})
                  </div>
                )}
              </VBox>
              <VBox>
                {t("askForPermissionDialog.email-to-ceo-or-chairperson")}
                <MTextInput model={c.receiverEmail} />
              </VBox>
            </Grid>
            <hr />
          </>
        ))}
      </Dialog>
    );
  },
);

export const TransferOwnershipDialog = observer(
  ({ state, actions }: { state: ReadyState; actions: PortfolioPageActions }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t("portfolio.transfer-ownership.title", {
          portfolioName: state.portfolio.label,
        })}
        onCancel={() => actions.closeTransferOwnershipDialog()}
        cancelLabel={t("close")}
        submitLabel={t("save")}
        isSubmitDisabled={
          !state.transferOwnershipModel?.value || isFailure(state.ownershipUserResult)
        }
        isSubmitting={state.isLoadingOwnershipTransfer}
        onSubmit={actions.sendOwnershipTransfer}
      >
        <VBox gap={"lg"}>
          <div>
            {t("portfolio.transfer-ownership.description", {
              userName: state.portfolioResponse.portfolio?.createdBy,
              email: state.portfolioResponse.portfolio?.createdEmail,
            })}
          </div>
          <VBox gap={"md"}>
            <strong>{t("portfolio.transfer-ownership.new-owner")}</strong>
            <div>
              {t("portfolio.transfer-ownership.choose.another.owner", {
                portfolioName: state.portfolio.label,
              })}
            </div>
            {isPending(state.ownershipUserResult) ? (
              <LoadingIndicator size={"md"} label={""} />
            ) : isOk(state.ownershipUserResult) ? (
              <>
                {state.transferOwnershipModel ? (
                  <MRadioButtonGroup model={state.transferOwnershipModel} />
                ) : (
                  <Alert
                    type={"info"}
                    message={{
                      key: "portfolio.ownership-transfer-alert-info",
                      params: {
                        portfolioName: state.portfolio.label,
                        adminLink: extractPortfolioContactsAdminUrl(state.portfolio),
                      },
                    }}
                  />
                )}
              </>
            ) : null}
          </VBox>
        </VBox>
      </Dialog>
    );
  },
);

export const EmptyPortfolioView = observer(
  ({
    portfolioName,
    openAddCompanyDialog,
    openPortfolioSettings,
    confirmDissolvePortfolio,
    openTransferOwnershipDialog,
  }: {
    portfolioName?: string;
    openAddCompanyDialog: () => void;
    openPortfolioSettings: () => void;
    confirmDissolvePortfolio: () => void;
    openTransferOwnershipDialog?: () => void;
  }) => {
    const { t } = useTranslation();
    return (
      <VBox grow={"golden"} justifyContent={"center"} fg={"subtle"}>
        <EmptyState
          illustration={"briefcase"}
          title={portfolioName ?? ""}
          description={t("portfolio.empty")}
          actions={
            <VBox gap={"xl"}>
              <Button
                variant={"secondary"}
                icon={"plus"}
                onClick={openAddCompanyDialog}
                label={t("portfolio.welcome.add-company")}
              />
              <LinkButton
                icon={"edit"}
                label={t("portfolio.options.menu.rename-portfolio")}
                onClick={openPortfolioSettings}
              />
              {openTransferOwnershipDialog && (
                <LinkButton
                  icon={"user-switch"}
                  label={t("portfolio.transfer-ownership")}
                  onClick={openTransferOwnershipDialog}
                />
              )}
              <LinkButton
                icon={"trash"}
                color={"error"}
                label={t("portfolio.dissolve-portfolio")}
                onClick={confirmDissolvePortfolio}
              />
            </VBox>
          }
        />
      </VBox>
    );
  },
);

export function ContactFooter() {
  const { t } = useTranslation();
  return (
    <Container maxWidth={"sm"} marginY={"lg"}>
      <VBox textAlign={"center"}>
        <Markdown value={t("portfolio.footer")} enableLinks />
      </VBox>
    </Container>
  );
}

const PortfolioActionsDropdown = observer(
  ({ state, actions }: { state: ReadyState; actions: PortfolioPageActions }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    const decisionsByCategory = getDecisionsByCategory({
      isBatchProject: true,
      checkedDecisions: state.checkedDecisions,
      paidSubscription: {
        ...allFeaturesIncluded,
        organisationNumber: "",
        subscriptionStatus: SubscriptionStatus.ACTIVE,
        subscriptionType: SubscriptionType.PRO,
        isActive: true,
      },
    });
    const companiesWithAdminAccess = state.userCompaniesInPortfolio.filter(
      (a) => a.accessType === AccessType.ADMIN,
    );
    const hasMultipleCompanies: boolean = companiesWithAdminAccess.length > 1;
    const hasAdminAccess = companiesWithAdminAccess.length > 0;

    return hasAdminAccess ? (
      <DropdownMenu
        button={{
          type: "button",
          variant: "secondary",
          label: t("portfolio.actions"),
          loading: state.isLoadingCustomRole,
          onClick: actions.getCustomRoleItems,
        }}
        items={
          !state.isLoadingCustomRole
            ? [
                {
                  type: "label",
                  label: t("portfolio.actions.admin-in-companies", {
                    count: state.adminsCompanyCount,
                  }),
                },
                {
                  type: "item",
                  label: t("portfolio.invite-admin"),
                  icon: "user-plus",
                  onSelect: () => actions.openInviteAdministratorDialog(),
                },
                ...(state.customRoleItem?.customRoleItems
                  ?.filter((f) => f.companies.length > 1)
                  .map<DropdownMenuItemType>((roleItem) => ({
                    type: "item",
                    label: t("portfolio.actions.invite-custom-role", {
                      customRoleName: getRoleNameByLanguage(roleItem.role, locale),
                    }),
                    icon: "user-plus",
                    onSelect: () => actions.openInviteCustomRoleDialog(roleItem),
                  })) ?? []),
                {
                  type: "item",
                  label: t("norway"),
                  icon: "plus",
                  onSelect: actions.companySearchActions.openNorwegianCompanySearch,
                },
                {
                  type: "item",
                  label: t("sweden"),
                  icon: "plus",
                  onSelect: actions.companySearchActions.openSwedishCompanySearch,
                },
                {
                  type: "item",
                  label: t("portfolio.actions.create-new-portfolio"),
                  icon: "folder-plus",
                  onSelect: actions.openCreatePortfolioDialog,
                },
                {
                  type: "item",
                  label: t("portfolio.export-shareholder-registers"),
                  icon: "download",
                  onSelect: actions.openExportShareholdersDialog,
                },
                {
                  type: "item",
                  label: t("portfolio.sign-shareholder-registers"),
                  icon: "signature",
                  tag: { label: { key: "dbot.new" }, color: "info", compact: true },
                  onSelect: actions.signShareholderRegistersActions.openDialog,
                },
                hasMultipleCompanies && {
                  type: "label",
                  label: t("portfolio.options.menu.start-portfolio-project"),
                },

                ...decisionsByCategory.flatMap((category) =>
                  category.decisions.map<DropdownMenuItemType | null>((type) =>
                    hasMultipleCompanies
                      ? {
                          type: "item",
                          label: <DecisionLabel decisionType={type.decisionType} />,
                          icon: "sparkle",
                          onSelect: () => {
                            actions.openCreateProjectDialog(type.decisionType);
                          },
                        }
                      : null,
                  ),
                ),
              ]
            : []
        }
      />
    ) : undefined;
  },
);

export const AskForPermissionForm: FC<{
  askForPermissionResult?: Result;
  onboardingCandidatesResult?: Result<OnboardingCandidateResponse>;
  receiverEmailAddress: StringFieldModel;
  companyRegisterKey: TMessageKey;
  company: string;
}> = observer(
  ({
    askForPermissionResult,
    onboardingCandidatesResult,
    receiverEmailAddress,
    companyRegisterKey,
    company,
  }) => {
    const { t } = useTranslation();
    return (
      <VBox>
        {isFailure(onboardingCandidatesResult) ? (
          <Alert
            type={"info"}
            icon={false}
            message={{
              key: "askForPermissionDialog.onboardingCandidatesResult.error",
              params: {
                company: company,
              },
            }}
          />
        ) : !isOk(askForPermissionResult) ? (
          <Alert
            type={"success"}
            icon={false}
            marginTop
            message={
              <div>
                {t("askForPermissionDialog.infoMessage", {
                  company: isOk(onboardingCandidatesResult)
                    ? onboardingCandidatesResult.data.companyName
                    : "",
                  companyRegister: t(companyRegisterKey),
                })}
              </div>
            }
          />
        ) : null}

        {!isOk(askForPermissionResult) &&
          isOk(onboardingCandidatesResult) &&
          onboardingCandidatesResult.data.namesWithRoles.map((it) => {
            return (
              <Box key={`${it.name}_${it.roleTypes}`}>
                {it.name} ({it.roleTypes.map((it) => t(getRoleTypeLabel(it))).join(", ")})
              </Box>
            );
          })}
        {!isOk(askForPermissionResult) && (
          <MTextInput
            label={t("askForPermissionDialog.email-to-ceo-or-chairperson")}
            model={receiverEmailAddress}
            autofocus={true}
          />
        )}
      </VBox>
    );
  },
);

const PortfolioCollapsibleCheckboxGroup = observer(function PortfolioCollapsibleCheckboxGroup({
  portfolioName,
  companies,
  action,
  highlightWords,
  initiallyExpanded,
}: {
  portfolioName: string;
  companies: PortfolioCompany[];
  action?: PortfolioCompaniesActions;
  highlightWords?: string[];
  initiallyExpanded?: boolean;
}) {
  const { t } = useTranslation();
  const allSelected = companies.every((c) => action?.isCompanySelected(c.organisationNumber));

  const toggleAll = (checked: boolean) => {
    companies.forEach((c) => {
      const isSelected = action?.isCompanySelected(c.organisationNumber) ?? false;
      if (checked !== isSelected) {
        action?.toggleCompanyInAddDialog(c.organisationNumber);
      }
    });
  };

  return (
    <CollapseList
      openLabel={t("show")}
      closeLabel={t("hide")}
      togglePosition={"start"}
      initiallyExpanded={initiallyExpanded ?? true}
      renderToggle={(toggleCollapse, label) => (
        <Box justifyContent="space-between">
          <Checkbox
            label={<HighlightText value={portfolioName} words={highlightWords} />}
            checked={allSelected}
            onChange={(e) => toggleAll(e.target.checked)}
          />
          <LinkButton label={label} onClick={toggleCollapse} />
        </Box>
      )}
    >
      <VBox paddingLeft={"lg"} paddingTop={"sm"}>
        {companies.map(({ name, organisationNumber }) => (
          <Checkbox
            label={<HighlightText value={name} words={highlightWords} key={organisationNumber} />}
            key={organisationNumber}
            value={organisationNumber}
            checked={action?.isCompanySelected(organisationNumber)}
            onChange={() => action?.toggleCompanyInAddDialog(organisationNumber)}
            disabled={action?.isCompanyDisabled?.(organisationNumber)}
          />
        ))}
      </VBox>
    </CollapseList>
  );
});

export const PortfolioCompaniesSelectView = observer(
  ({ props }: { props: PortfolioCompaniesSelectProps }) => {
    const { t } = useTranslation();
    const filteredCompanies = filterSelectableCompanies(props);
    const highlightWords = props.companySearchFieldModel?.inputValue
      ? tokenizeFilterString(props.companySearchFieldModel.inputValue)
      : [];

    const groups = props.isContextCustomRole ? groupCompaniesByPortfolio(filteredCompanies) : null;

    return (
      <VBox gap={"md"}>
        <Box>
          <MTextInput
            model={props.companySearchFieldModel}
            icon={"magnifying-glass"}
            placeholder={t("portfolioContacts.nameFilterPlaceholder")}
            type={"search"}
          />
        </Box>
        <Box alignItems={"center"} justifyContent={"space-between"} marginBottom={"md"}>
          <Box gap={"md"}>
            <LinkButton label={t("select-all")} onClick={props.action?.toggleAllCompanies} />
            <LinkButton label={t("select-none")} onClick={props.action?.deselectAllCompanies} />
          </Box>
          {props.action?.filterSelectedCompanies && (
            <LinkButton
              label={
                props.showSelectedAll
                  ? t("showAll")
                  : t("portfolio.company.portfolio-project.show-selected-companies", {
                      count: props.selectedCompaniesCount,
                    })
              }
              onClick={props.action?.filterSelectedCompanies}
            />
          )}
        </Box>
        <VBox className={"overflow-y-auto max-h-96"}>
          {groups
            ? groups.map(({ portfolioName, companies, portfolioId }) => (
                <PortfolioCollapsibleCheckboxGroup
                  key={portfolioId}
                  portfolioName={portfolioName || t("other-companies")}
                  companies={companies}
                  action={props.action}
                  highlightWords={highlightWords}
                />
              ))
            : filteredCompanies.map(({ name, organisationNumber }) => (
                <Checkbox
                  label={
                    <HighlightText value={name} words={highlightWords} key={organisationNumber} />
                  }
                  key={organisationNumber}
                  value={organisationNumber}
                  checked={props.action?.isCompanySelected(organisationNumber)}
                  onChange={() => props.action?.toggleCompanyInAddDialog(organisationNumber)}
                />
              ))}
        </VBox>
      </VBox>
    );
  },
);
